/**
 * The page the light preview renders (light-preview plan F2).
 *
 * It replaces what Vite served from inside the WebContainer: the template's `index.html` plus the
 * compiled app. Everything it needs is either inlined or served from our own origin — the import
 * map points at the prebuilt vendor bundle, and Tailwind's browser build is vendored too, so the
 * preview makes no third-party request.
 *
 * Pure string building, so it stays unit-testable in the node test env.
 */

import { stripTailwindDirectives } from './compiler';

export interface PreviewDocumentInput {
  /** Compiled application JavaScript (ESM), with bare imports left for the import map. */
  appJs: string;
  /**
   * The project's stylesheets as written, directives included. With a Tailwind runtime they are
   * given to it as source; without one, what plain CSS cannot express is dropped.
   */
  css: string;
  /** Specifier → URL, from the vendor bundle's `importmap.json`. */
  importMap: Readonly<Record<string, string>>;
  /** URL of the vendored Tailwind browser build; omitted in tests and plain-CSS projects. */
  tailwindRuntimeUrl?: string;
  /**
   * Stylesheets that belong to a package rather than to the project (US 359: Framework7's own
   * CSS). Linked **before** the project's styles, so the startup's tokens override the
   * framework's defaults rather than the other way round.
   */
  vendorStyleUrls?: readonly string[];
  /** The template's `tailwind.config.js`, already reduced to a plain object literal. */
  tailwindConfig?: string;
  /**
   * The render/crash reporter, inlined as a module before the app. A module, not a classic
   * script: it imports from the project, and an IIFE build turns those into `require()` calls
   * the browser rejects.
   */
  bridgeJs?: string;
  /** Shown in the tab title of a detached preview; harmless inside the workbench iframe. */
  title?: string;
  /** What this page keeps from the project's own `index.html` (`projectHtmlShell`). */
  projectHtml?: ProjectHtmlShell;
  /**
   * The template's platform stylesheet (the mobile template's `src/ideoz/platform.css`), added
   * after the project's styles. It belongs to the template, not the project, so a fix to it
   * reaches prototypes built before the fix.
   */
  platformCss?: string;
}

/**
 * The parts of a project's `index.html` this page carries over. The preview builds its own page,
 * so without this a mobile prototype lost its seeded head: the viewport with viewport-fit=cover,
 * the style that fills the screen and hides the scrollbars, and `class="dark"` - and previewed
 * with a desktop scrollbar down the phone (27 Sep 2026). Scripts are not carried: the page loads
 * the compiled app itself.
 */
export interface ProjectHtmlShell {
  viewport?: string;
  htmlClass?: string;
  styles: string[];
}

export function projectHtmlShell(indexHtml: string | undefined): ProjectHtmlShell {
  if (!indexHtml) return { styles: [] };
  const viewport = /<meta\s+name=["']viewport["']\s+content=["']([^"']*)["']/i.exec(indexHtml)?.[1];
  const htmlClass = /<html\b[^>]*\bclass=["']([^"']*)["']/i.exec(indexHtml)?.[1];
  const styles = [...indexHtml.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)]
    .map((m) => m[1].trim())
    .filter(Boolean);
  return { viewport, htmlClass, styles };
}

const escapeAttr = (value: string): string => value.replace(/[<>&"]/g, '');

const escapeForScript = (value: string): string => value.replace(/<\/script/gi, '<\\/script');

/**
 * `type="module"` scripts are deferred and run in document order, so the bridge (which only
 * listens) is inlined before the app, and the app's own `#root` is present before either runs.
 */
export function buildPreviewDocument(input: PreviewDocumentInput): string {
  const {
    appJs,
    css,
    importMap,
    tailwindRuntimeUrl,
    tailwindConfig,
    bridgeJs,
    vendorStyleUrls = [],
    title = 'Preview',
    projectHtml,
    platformCss,
  } = input;

  const viewport = escapeAttr(projectHtml?.viewport || 'width=device-width, initial-scale=1');
  const htmlClass = projectHtml?.htmlClass ? ` class="${escapeAttr(projectHtml.htmlClass)}"` : '';
  const projectStyles = (projectHtml?.styles ?? [])
    .map((css) => `    <style>\n${css.replace(/<\/style/gi, '')}\n    </style>`)
    .join('\n');

  const vendorStyles = vendorStyleUrls
    .map((url) => `    <link rel="stylesheet" href="${url}" />`)
    .join('\n');

  const tailwind = tailwindRuntimeUrl
    ? [
        `    <script src="${tailwindRuntimeUrl}"></script>`,
        tailwindConfig
          ? `    <script>tailwind.config = ${escapeForScript(tailwindConfig)};</script>`
          : '',
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  /**
   * `type="text/tailwindcss"` is what makes the runtime compile this stylesheet rather than the
   * browser parse it — which is the only way `@tailwind` and `@apply` mean anything here. The
   * template's base layer is two `@apply` rules that set the page's own background, text colour
   * and default border colour from its design tokens; dropping them left a dark prototype
   * previewing on a white page, with every un-coloured border in Tailwind's default grey.
   *
   * Without a runtime (the unit tests, a project with ordinary CSS) the directives are dropped
   * instead, because nothing would ever resolve them.
   */
  const styles = tailwindRuntimeUrl
    ? `    <style type="text/tailwindcss">\n${css}\n    </style>`
    : `    <style>\n${stripTailwindDirectives(css)}\n    </style>`;

  return `<!doctype html>
<html lang="en"${htmlClass}>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="${viewport}" />
    <title>${title}</title>
    <script type="importmap">
${JSON.stringify({ imports: importMap }, null, 6)}
    </script>
${tailwind}
${vendorStyles}
${projectStyles}
${styles}
${platformCss ? `    <style data-ideoz-platform>\n${platformCss.replace(/<\/style/gi, '')}\n    </style>` : ''}
  </head>
  <body>
    <div id="root"></div>
${bridgeJs ? `    <script type="module">\n${escapeForScript(bridgeJs)}\n    </script>` : ''}
    <script type="module">
${escapeForScript(appJs)}
    </script>
  </body>
</html>
`;
}

/**
 * A document URL the preview iframe can load. Blob URLs keep the page out of the host's origin,
 * so generated code cannot reach the workbench's storage or cookies.
 */
export function createPreviewDocumentUrl(html: string): string {
  return URL.createObjectURL(new Blob([html], { type: 'text/html' }));
}

/**
 * `tailwind.config.js` is an ES module with an import and a default export; the browser build
 * wants a plain object. Returns null when the shape is not the one the template ships, so the
 * caller falls back to Tailwind's defaults instead of injecting broken JavaScript.
 */
export function tailwindConfigObjectSource(configSource: string): string | null {
  const start = configSource.indexOf('export default');
  if (start === -1) return null;
  const object = configSource.slice(start + 'export default'.length).trim().replace(/;\s*$/, '');
  if (!object.startsWith('{')) return null;
  // `content` scans files that do not exist here, and plugins are Node modules.
  return object
    .replace(/content\s*:\s*\[[^\]]*\]\s*,?/, '')
    .replace(/plugins\s*:\s*\[[^\]]*\]\s*,?/, '');
}
